// React Dependencies
import React, { useEffect, useMemo, useState } from 'react';

// External Dependencies
import { Add } from '@material-ui/icons';
import MaterialTable from 'material-table';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

// Internal Dependencies
import MText from '../../../../../components/Common/MText';
import MButton from '../../../../../components/MButton';
import LoyaltyMenuDescriptionBox from '../../../../../components/LoyaltyMenuDescriptionBox';
import Colours from '../../../../../styles/colours';
import { IsEmpty } from '../../../../../common/checks';
import EditVoucher from './EditVoucher';
import withLoyaltyConnect from '../WithLoyaltyConnect';
import ActionModal from './ActionModal';
import { Status } from '../../../../../common/constants';
import ApiService from '../../../../../services/apiService';
import { VoucherActionType } from '../constants';

const Voucher = (props) => {
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [actionType, setActionType] = useState(VoucherActionType.Delete);

  const onDeleteVoucher = (id) => {
    if (!IsEmpty(selectedVoucher)) {
      const filteredVouchers = props.loyaltyProgram?.vouchers?.filter((v) => v.id !== id);
      props.updateLoyaltyCardAction({ vouchers: filteredVouchers });
    }
  };

  const defaultColumns = [
    {
      title:       'ID',
      field:       'id',
      editable:    'never',
      headerStyle: { fontSize: 12 },
      render:      (row) => (
        <MButton
          text={row.id}
          textColour={Colours.Blue}
          textSize={12}
          variant="text"
          className="pl-0"
          textStyle={{ textDecoration: 'underline', align: 'left' }}
          onClick={() => {
            const voucher = props.loyaltyProgram.vouchers.find((vc) => vc.id === row.id);
            props.updateLoyaltyCardAction({ selectedVoucherId: voucher.id });
          }}
        />
      ),
    },
    {
      title:       'Design',
      field:       'design',
      editable:    'never',
      headerStyle: { fontSize: 12 },
      render:      (row) => (!IsEmpty(row.design) ? <img src={row.design} alt="voucher-design" /> : null),
    },
    {
      title:       'Type',
      field:       'type',
      editable:    'never',
      cellStyle:   { width: '20%' },
      headerStyle: { fontSize: 12 },
      render:      (row) => (
        <MText
          text={row.isProduct ? 'Free Item/s' : 'Dollar/Offer'}
          colour={row.status !== Status.Deactivated ? Colours.Black : Colours.Gray5}
          size={12}
        />
      ),
    },
    {
      title:       'Qty',
      field:       'qty',
      editable:    'never',
      headerStyle: { fontSize: 12 },
      cellStyle:   { width: '10%' },
      render:      (row) => (
        <MText
          text={!IsEmpty(row.issuableQty) ? String(row.issuableQty) : 'Unlimited'}
          colour={row.status !== Status.Deactivated ? Colours.Black : Colours.Gray5}
          size={12}
        />
      ),
    },
    {
      title:       'Bal. Qty',
      field:       'balQty',
      editable:    'never',
      headerStyle: { fontSize: 12 },
      cellStyle:   { width: '15%' },
      render:      (row) => (
        <MText
          text={row.balQty}
          colour={row.status !== Status.Deactivated ? Colours.Black : Colours.Gray5}
          size={12}
        />
      ),
    },
    {
      title:  '',
      render: (row) => {
        if (row.status !== Status.Deactivated) {
          return (
            <div className="w-100 d-flex flex-column align-items-center" style={{ rowGap: 8 }}>
              {
                props.loyaltyProgram.status === Status.Live && row.status === Status.Draft && (
                  <MButton
                    onClick={() => {
                      setShowActionModal(true);
                      setSelectedVoucher(row);
                      setActionType(VoucherActionType.Activate);
                    }}
                    textSize={12}
                    textStyle={{ textAlign: 'center' }}
                    text="Activate"
                    variant="text"
                    className="w-100 border"
                  />
                )
              }
              <MButton
                onClick={() => {
                  setShowActionModal(true);
                  setSelectedVoucher(row);
                  setActionType(row.status === Status.Draft ? VoucherActionType.Delete : VoucherActionType.Deactivate);
                }}
                textSize={12}
                textStyle={{ textAlign: 'center' }}
                text={row.status === Status.Draft ? VoucherActionType.Delete : VoucherActionType.Deactivate}
                variant="text"
                className="w-100 border"
              />
            </div>
          );
        }
      },
      cellStyle: { width: '5%' },
    },
  ];

  const columns = useMemo(() => defaultColumns);

  const nonDeactivatedVouchers = props.loyaltyProgram.vouchers.filter((vc) => vc.status !== Status.Deactivated);

  const canCreateNewVoucher = !nonDeactivatedVouchers.some((vc) => IsEmpty(vc.issuableQty))
    && (nonDeactivatedVouchers.filter((vc) => vc.balQty !== 0).length < 2)
    && (props.loyaltyProgram.status !== Status.Deactivated);

  const handleAddVoucher = async () => {
    try {
      props.dispatchStartSavingLC();
      const res = await ApiService.createLoyaltyCardGift(props.page.id, props.match.params.loyaltyCardId);

      const newVoucher =  {
        id:               res.id,
        isProduct:        res.isProduct,
        minSpend:         res.minSpend,
        design:           res.design,
        expiryDate:       res.expiryDate,
        validityInMonths: res.validityInMonths,
        label:            res.label,
        terms:            res.terms,
        issuableQty:      res.issuableQty,
        balQty:           null, // By default, the new voucher created will have an unlimited qty so we don't need to set the balQty
        status:           res.status,
      };

      const firstDeactivatedVoucherIndex = props.loyaltyProgram.vouchers.findIndex((vc) => vc.status === Status.Deactivated);

      let updatedVouchers = [...props.loyaltyProgram.vouchers];
      if (firstDeactivatedVoucherIndex === -1) {
        updatedVouchers = [
          ...updatedVouchers,
          newVoucher,
        ];
      } else {
        updatedVouchers = [
          ...updatedVouchers.slice(0, firstDeactivatedVoucherIndex),
          newVoucher,
          ...updatedVouchers.slice(firstDeactivatedVoucherIndex),
        ];
      }

      props.updateLoyaltyCardAction({
        vouchers:          updatedVouchers,
        selectedVoucherId: newVoucher.id,
      });

      toast.success('Voucher created');
    } catch (err) {
      toast.error(err?.message || 'Failed to create a new voucher');
    } finally {
      props.dispatchFinishSavingLC();
    }
  };

  const onCloseDeleteModal = () => {
    setShowActionModal(false);
    setSelectedVoucher(null);
  };

  useEffect(() => {
    props.hideSaveBtnAction();

    return () => {
      props.showSaveBtnAction(); // Reset the showSaveBtn state after unmounting the voucher component
    };
  }, []);

  return (
    <div>
      <div className="row">
        <div className="col-12 col-xl-10">
          <LoyaltyMenuDescriptionBox
            text="Vouchers allows users to redeem rewards after the required number of stamps has been collected. The reward can be a discount (eg. X dollars off, X% off, etc.) or a free item (coffee, toy, bag, etc). Vouchers can be customized and will have separate Terms & Conditions from the card."
          />
        </div>
      </div>
      {
        !IsEmpty(props.loyaltyProgram.selectedVoucherId) ? (
          <EditVoucher match={props.match} />
        ) : (
          <div>
            <div className="d-flex flex-row align-items-center mt-3">
              <MText text="Vouchers" />
              <MButton
                disableElevation
                style={{
                  border: '1px solid black',
                }}
                disabled={!canCreateNewVoucher}
                text="Add Voucher"
                type="button"
                className="ml-4"
                onClick={handleAddVoucher}
                icon={() => <Add style={{ fontSize: 13 }} />}
              />
            </div>
            <MaterialTable
              title=""
              style={{ marginTop: 32 }}
              columns={columns}
              components={{ Toolbar: () => null }}
              options={{ search: false, sorting: false, debounceInterval: 500, emptyRowsWhenPaging: false }}
              data={props.loyaltyProgram.vouchers.map((vc) => ({
                ...vc,
                type: vc.isProduct ? 'Free Item/s' : 'Dollar/Offer',
              }))}
            />
            <ActionModal
              open={showActionModal}
              selectedVoucher={selectedVoucher}
              loyaltyCardId={props.match.params.loyaltyCardId}
              pageId={props.page.id}
              type={actionType}
              onClose={onCloseDeleteModal}
              onComplete={() => {
                if (actionType === VoucherActionType.Delete) {
                  onDeleteVoucher(selectedVoucher?.id);
                } else if (actionType === VoucherActionType.Activate || actionType === VoucherActionType.Deactivate) {
                  props.updateLoyaltyCardAction({
                    vouchers: props.loyaltyProgram.vouchers.map((voucher) => (
                      voucher.id === selectedVoucher?.id
                        ? {
                          ...voucher,
                          status: actionType === VoucherActionType.Activate ? Status.Activated : Status.Deactivated,
                        }
                        : voucher
                    )),
                  });
                }
              }}
            />
          </div>
        )
      }
    </div>
  );
};

Voucher.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      loyaltyCardId: PropTypes.string,
    }),
  }).isRequired,
  loyaltyProgram: PropTypes.shape({
    vouchers: PropTypes.arrayOf(PropTypes.shape({
      id:        PropTypes.string,
      isProduct: PropTypes.bool,
      design:    PropTypes.string,
      qty:       PropTypes.number,
      balQty:    PropTypes.number,
    })),
    selectedVoucherId: PropTypes.string,
    status:            PropTypes.oneOf([Status.Draft, Status.Live, Status.Deactivated]),
  }).isRequired,
  page: PropTypes.shape({
    id:       PropTypes.number,
    pageLogo: PropTypes.string,
  }).isRequired,
  updateLoyaltyCardAction: PropTypes.func.isRequired,
  showSaveBtnAction:       PropTypes.func.isRequired,
  hideSaveBtnAction:       PropTypes.func.isRequired,
  dispatchStartSavingLC:   PropTypes.func.isRequired,
  dispatchFinishSavingLC:  PropTypes.func.isRequired,
};

export default withLoyaltyConnect(Voucher);
